/**
 * Progressive RSS feed loading — surfaces articles as proxy chunks complete.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataSourceConfig } from '../services/dataSourceIntegrations';
import {
  fetchRSSFeedsProgressive,
  RSSFeedItem,
} from '../services/usGovernmentIntegrations';
import { computeRssFeedLoadProgress } from '../utils/industryNewsFeedProgress';

export interface UseProgressiveRssFeedsOptions {
  config: DataSourceConfig;
  projectId?: string;
  enabled?: boolean;
  refreshInterval?: number | false;
}

export function useProgressiveRssFeeds({
  config,
  projectId,
  enabled = true,
  refreshInterval = false,
}: UseProgressiveRssFeedsOptions) {
  const [articles, setArticles] = useState<RSSFeedItem[]>([]);
  const [feedsLoadedCount, setFeedsLoadedCount] = useState(0);
  const [totalFeedCount, setTotalFeedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadGenerationRef = useRef(0);

  const queryKey = useMemo(
    () =>
      [
        projectId ?? '',
        config.filters?.keywords?.join('|') ?? '',
        config.filters?.feedIds?.join('|') ?? '',
        config.filters?.geographic_zone ?? '',
      ].join(':'),
    [
      projectId,
      config.filters?.keywords,
      config.filters?.feedIds,
      config.filters?.geographic_zone,
    ]
  );

  const configRef = useRef(config);
  configRef.current = config;

  const loadFeeds = useCallback(async () => {
    if (!projectId || !enabled) {
      return;
    }

    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;
    setIsLoading(true);
    setError(null);
    setArticles([]);
    setFeedsLoadedCount(0);

    try {
      await fetchRSSFeedsProgressive(configRef.current, projectId, {
        onArticlesUpdate: (nextArticles) => {
          if (loadGenerationRef.current !== generation) {
            return;
          }
          setArticles(nextArticles);
        },
        onFeedProgress: ({ feedsLoaded, totalFeeds }) => {
          if (loadGenerationRef.current !== generation) {
            return;
          }
          setFeedsLoadedCount(feedsLoaded);
          setTotalFeedCount(totalFeeds);
        },
      });
    } catch (loadError) {
      if (loadGenerationRef.current !== generation) {
        return;
      }
      setError(loadError instanceof Error ? loadError : new Error(String(loadError)));
    } finally {
      if (loadGenerationRef.current === generation) {
        setIsLoading(false);
      }
    }
  }, [enabled, projectId]);

  useEffect(() => {
    if (!enabled || !projectId) {
      setArticles([]);
      setFeedsLoadedCount(0);
      setTotalFeedCount(0);
      setIsLoading(false);
      return;
    }

    void loadFeeds();
  }, [enabled, loadFeeds, projectId, queryKey]);

  useEffect(() => {
    if (!refreshInterval || !enabled || !projectId) {
      return;
    }

    const interval = window.setInterval(() => {
      void loadFeeds();
    }, refreshInterval);

    return () => window.clearInterval(interval);
  }, [enabled, loadFeeds, projectId, queryKey, refreshInterval]);

  const feedLoadProgress = useMemo(
    () =>
      computeRssFeedLoadProgress({
        feedsLoadedCount,
        totalFeedCount,
        isLoadingFeeds: isLoading,
      }),
    [feedsLoadedCount, isLoading, totalFeedCount]
  );

  return {
    articles,
    isLoading: isLoading && articles.length === 0,
    isFetching: isLoading,
    feedLoadProgress,
    error,
    refetch: loadFeeds,
  };
}
